/**
 * Human-readable renderer for a sandbox action's execution result.
 *
 * Replaces the old raw-JSON `<pre>` dump. Handles the credential-reuse case
 * (a friendly note plus the inputs the resource was first minted with) and
 * falls back to a label → value list for any other result shape. Sensitive
 * inputs arrive pre-masked from the server as `***`.
 */

import React from 'react';
import { Text, Flex, Box } from '@chatsdet/ui';

/** Render a single result value — recurses into nested objects/arrays so we
 *  never fall back to raw JSON. */
function ResultValue({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return (
      <Text size="sm" className="text-muted-foreground">
        —
      </Text>
    );
  }
  if (typeof value === 'boolean') {
    return <Text size="sm">{value ? 'Yes' : 'No'}</Text>;
  }
  if (typeof value === 'object') {
    return Array.isArray(value) ? (
      <Flex direction="column" gap="xs">
        {value.map((v, i) => (
          <ResultValue key={i} value={v} />
        ))}
      </Flex>
    ) : (
      <ResultKeyValueList data={value as Record<string, unknown>} />
    );
  }
  return (
    <Text size="sm" className="font-mono break-all">
      {String(value)}
    </Text>
  );
}

/** A label → value list for an object, styled to match the actions panel. */
function ResultKeyValueList({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data);
  if (entries.length === 0) return null;
  return (
    <Flex direction="column" gap="xs" className="mt-1">
      {entries.map(([key, value]) => (
        <Flex key={key} align="baseline" justify="between" gap="md">
          <Text size="xs" className="text-muted-foreground shrink-0">
            {key}
          </Text>
          <Box className="min-w-0 text-right">
            <ResultValue value={value} />
          </Box>
        </Flex>
      ))}
    </Flex>
  );
}

export function ActionResultView({ result }: { result: Record<string, unknown> }) {
  if (result.reused === true) {
    const raw = result.originalInputs;
    const originalInputs =
      raw && typeof raw === 'object' && !Array.isArray(raw)
        ? (raw as Record<string, unknown>)
        : null;
    return (
      <Box>
        <Text size="sm" className="text-muted-foreground">
          Reused an existing resource — no new one was created.
        </Text>
        {originalInputs && Object.keys(originalInputs).length > 0 && (
          <Box className="mt-2">
            <Text
              size="xs"
              weight="medium"
              className="uppercase tracking-wide text-muted-foreground"
            >
              Inputs from the original run
            </Text>
            <ResultKeyValueList data={originalInputs} />
          </Box>
        )}
      </Box>
    );
  }

  if (Object.keys(result).length === 0) return null;
  return <ResultKeyValueList data={result} />;
}
